import type { OrderStatus, RunStatus } from "@the-drop/types";
import { fetchWithClerkJwt } from "./fetch-with-clerk";

/**
 * Mirrors the API's `delivery_payment_method`. `prepaid` (ADR-125) means an
 * ecommerce partner already charged the customer; show "Prepaid via partner"
 * instead of an amount due.
 */
export type DispensaryOrderPaymentMethod = "ach" | "cash_at_delivery" | "prepaid";

export type DispensaryOrderListItem = {
  id: string;
  status: OrderStatus;
  totalCents: number;
  deliveryPaymentMethod?: DispensaryOrderPaymentMethod;
  createdAt: string;
  updatedAt: string;
  customerFullName: string;
  deliveryCity: string;
  deliveryZip: string;
};

export type DispensaryOrdersListResponse = {
  orders: DispensaryOrderListItem[];
};

/** A row in the org-wide "All locations" view — a list item plus its location. */
export type OrgOrderListItem = DispensaryOrderListItem & {
  dispensaryId: string;
  locationLabel: string | null;
  locationName: string;
};

export type OrgOrdersListResponse = {
  orders: OrgOrderListItem[];
  /** Opaque keyset cursor for the next page; null when no more. */
  nextCursor: string | null;
};

/** `GET /api/v1/orgs/:orgId/orders` — aggregate orders across the org's locations. */
export async function fetchOrgOrders(
  getToken: () => Promise<string | null>,
  orgId: string,
  opts?: { limit?: number; cursor?: string },
): Promise<OrgOrdersListResponse> {
  const q = new URLSearchParams();
  if (opts?.limit != null) q.set("limit", String(opts.limit));
  if (opts?.cursor != null) q.set("cursor", opts.cursor);
  const qs = q.toString();
  const path = `/api/v1/orgs/${encodeURIComponent(orgId)}/orders${qs ? `?${qs}` : ""}`;
  const res = await fetchWithClerkJwt(getToken, path);
  if (!res.ok) {
    throw new Error(`org_orders_http_${res.status}`);
  }
  return (await res.json()) as OrgOrdersListResponse;
}

export type DispensaryOrderLineItem = {
  id: string;
  metrcPackageUid: string;
  productName: string;
  productCategory: string;
  quantity: string;
  quantityUnit: string;
  unitPriceCents: number;
  lineTotalCents: number;
};

export type DispensaryOrderDetailResponse = {
  salesDeliveryStopId: string | null;
  order: {
    id: string;
    status: OrderStatus;
    customerId: string;
    customerFullName: string;
    deliveryAddressLine1: string;
    deliveryAddressLine2: string | null;
    deliveryCity: string;
    deliveryState: string;
    deliveryZip: string;
    subtotalCents: number;
    taxCents: number;
    deliveryFeeCents: number;
    coSurchargeCents: number;
    totalCents: number;
    deliveryPaymentMethod?: DispensaryOrderPaymentMethod;
    source: string;
    purchaseLimitsPassed: boolean;
    loadValueCheckPassed: boolean;
    orderFlowerOz: string;
    orderConcentrateG: string;
    orderThcMg: string;
    createdAt: string;
    updatedAt: string;
    confirmedAt: string | null;
    confirmationNotes: string | null;
    cancelledAt: string | null;
    paymentAuthorizedAt: string | null;
    /** ADR-124 — customer's delivery note from a partner checkout; read-only for staff. */
    deliveryInstructions: string | null;
  };
  lineItems: DispensaryOrderLineItem[];
};

export async function fetchDispensaryOrders(
  getToken: () => Promise<string | null>,
  dispensaryId: string,
  opts?: { limit?: number; offset?: number },
): Promise<DispensaryOrdersListResponse> {
  const q = new URLSearchParams();
  if (opts?.limit != null) q.set("limit", String(opts.limit));
  if (opts?.offset != null) q.set("offset", String(opts.offset));
  const qs = q.toString();
  const path = `/api/v1/dispensaries/${encodeURIComponent(dispensaryId)}/orders${qs ? `?${qs}` : ""}`;
  const res = await fetchWithClerkJwt(getToken, path);
  if (!res.ok) {
    throw new Error(`orders_http_${res.status}`);
  }
  return (await res.json()) as DispensaryOrdersListResponse;
}

export async function fetchDispensaryOrderDetail(
  getToken: () => Promise<string | null>,
  dispensaryId: string,
  orderId: string,
): Promise<DispensaryOrderDetailResponse> {
  const path = `/api/v1/dispensaries/${encodeURIComponent(dispensaryId)}/orders/${encodeURIComponent(orderId)}`;
  const res = await fetchWithClerkJwt(getToken, path);
  if (res.status === 404) {
    throw new Error("order_not_found");
  }
  if (!res.ok) {
    throw new Error(`order_detail_http_${res.status}`);
  }
  return (await res.json()) as DispensaryOrderDetailResponse;
}

export type ConfirmOrderResponse = {
  orderId: string;
  status: string;
  confirmedAt: string;
  alreadyConfirmed: boolean;
  dispatchAlerts: unknown[];
};

export async function confirmDispensaryOrder(
  getToken: () => Promise<string | null>,
  dispensaryId: string,
  orderId: string,
  confirmationNotes?: string | null,
): Promise<ConfirmOrderResponse> {
  const path = `/api/v1/dispensaries/${encodeURIComponent(dispensaryId)}/orders/${encodeURIComponent(orderId)}/confirm`;
  const res = await fetchWithClerkJwt(getToken, path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ confirmationNotes: confirmationNotes ?? null }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw Object.assign(new Error("confirm_failed"), { status: res.status, body });
  }
  return (await res.json()) as ConfirmOrderResponse;
}

export type DispensaryActiveRunItem = {
  id: string;
  status: RunStatus;
  transporterId: string;
  driverId: string;
  driverFullName: string;
  updatedAt: string;
  metrcManifestNumber: string | null;
};

export async function fetchDispensaryActiveRuns(
  getToken: () => Promise<string | null>,
  dispensaryId: string,
): Promise<{ runs: DispensaryActiveRunItem[] }> {
  const path = `/api/v1/dispensaries/${encodeURIComponent(dispensaryId)}/runs/active`;
  const res = await fetchWithClerkJwt(getToken, path);
  if (!res.ok) {
    throw new Error(`runs_http_${res.status}`);
  }
  return (await res.json()) as { runs: DispensaryActiveRunItem[] };
}

/** Active runs across the org's stores ("All locations"). */
export async function fetchOrgActiveRuns(
  getToken: () => Promise<string | null>,
  orgId: string,
): Promise<{ runs: DispensaryActiveRunItem[] }> {
  const path = `/api/v1/orgs/${encodeURIComponent(orgId)}/runs/active`;
  const res = await fetchWithClerkJwt(getToken, path);
  if (!res.ok) {
    throw new Error(`org_runs_http_${res.status}`);
  }
  return (await res.json()) as { runs: DispensaryActiveRunItem[] };
}

export type DispensarySettlementBatchItem = {
  id: string;
  status: string;
  periodStart: string;
  periodEnd: string;
  deliveryCount: number;
  successfulDeliveryCount: number;
  transporterFeeTotalCents: number;
  dispensaryPlatformFeeTotalCents: number;
  transporterPlatformFeeTotalCents: number;
  transporterName: string;
  approvalRequestedAt: string | null;
  approvedAt: string | null;
  createdAt: string;
};

/** A settlement batch in the org-wide view, tagged with its location. */
export type OrgSettlementBatchItem = DispensarySettlementBatchItem & {
  dispensaryId: string;
  locationLabel: string | null;
  locationName: string;
};

/** Settlement batches across the org's stores ("All locations", read-only). */
export async function fetchOrgSettlements(
  getToken: () => Promise<string | null>,
  orgId: string,
  tab: "pending" | "history",
): Promise<{ batches: OrgSettlementBatchItem[] }> {
  const q = new URLSearchParams({ tab });
  const path = `/api/v1/orgs/${encodeURIComponent(orgId)}/settlements?${q.toString()}`;
  const res = await fetchWithClerkJwt(getToken, path);
  if (!res.ok) {
    throw new Error(`org_settlements_http_${res.status}`);
  }
  return (await res.json()) as { batches: OrgSettlementBatchItem[] };
}

export async function fetchDispensarySettlements(
  getToken: () => Promise<string | null>,
  dispensaryId: string,
  tab: "pending" | "history",
): Promise<{ batches: DispensarySettlementBatchItem[] }> {
  const q = new URLSearchParams({ tab });
  const path = `/api/v1/dispensaries/${encodeURIComponent(dispensaryId)}/settlements?${q.toString()}`;
  const res = await fetchWithClerkJwt(getToken, path);
  if (!res.ok) {
    throw new Error(`settlements_http_${res.status}`);
  }
  return (await res.json()) as { batches: DispensarySettlementBatchItem[] };
}

export async function approveDispensarySettlement(
  getToken: () => Promise<string | null>,
  dispensaryId: string,
  batchStatementId: string,
  approvalNotes?: string | null,
): Promise<{ batchStatementId: string; approvedAt: string; idempotent: boolean }> {
  const path = `/api/v1/dispensaries/${encodeURIComponent(dispensaryId)}/settlements/${encodeURIComponent(batchStatementId)}/approve`;
  const res = await fetchWithClerkJwt(getToken, path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ approvalNotes: approvalNotes ?? null }),
  });
  if (!res.ok) {
    throw new Error(`approve_settlement_http_${res.status}`);
  }
  return (await res.json()) as { batchStatementId: string; approvedAt: string; idempotent: boolean };
}

export type DispensarySettlementLineItem = {
  id: string;
  stopId: string;
  salesOrderId: string;
  customerName: string;
  deliveredAt: string;
  orderValueCents: number;
  transporterFeeCents: number;
  dispensaryPlatformFeeCents: number;
  transporterPlatformFeeCents: number;
};

export async function fetchDispensarySettlementLineItems(
  getToken: () => Promise<string | null>,
  dispensaryId: string,
  batchStatementId: string,
): Promise<{ lineItems: DispensarySettlementLineItem[] }> {
  const path = `/api/v1/dispensaries/${encodeURIComponent(dispensaryId)}/settlements/${encodeURIComponent(batchStatementId)}/line-items`;
  const res = await fetchWithClerkJwt(getToken, path);
  if (!res.ok) {
    throw new Error(`settlement_line_items_http_${res.status}`);
  }
  return (await res.json()) as { lineItems: DispensarySettlementLineItem[] };
}

export async function removeDispensarySettlementLineItem(
  getToken: () => Promise<string | null>,
  dispensaryId: string,
  batchStatementId: string,
  lineItemId: string,
): Promise<{ batchStatementId: string }> {
  const path = `/api/v1/dispensaries/${encodeURIComponent(dispensaryId)}/settlements/${encodeURIComponent(batchStatementId)}/line-items/${encodeURIComponent(lineItemId)}`;
  const res = await fetchWithClerkJwt(getToken, path, { method: "DELETE" });
  if (!res.ok) {
    throw new Error(`remove_settlement_line_http_${res.status}`);
  }
  return (await res.json()) as { batchStatementId: string };
}

export async function submitDispensarySettlementForApproval(
  getToken: () => Promise<string | null>,
  dispensaryId: string,
  batchStatementId: string,
): Promise<{ batchStatementId: string; idempotent: boolean }> {
  const path = `/api/v1/dispensaries/${encodeURIComponent(dispensaryId)}/settlements/${encodeURIComponent(batchStatementId)}/submit-for-approval`;
  const res = await fetchWithClerkJwt(getToken, path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    if (res.status === 409 && body.error === "empty_batch") {
      throw new Error("submit_settlement_empty_batch");
    }
    throw new Error(`submit_settlement_http_${res.status}`);
  }
  return (await res.json()) as { batchStatementId: string; idempotent: boolean };
}

/**
 * ADR-130 — a storefront (POS) delivery order Drop refused to import, with
 * what Drop did about it in the POS. Mirrors the API's `pos_order_refusals`.
 */
export type DispensaryPosOrderRefusal = {
  id: string;
  posAdapter: string;
  posOrderId: string;
  posTransactionId: string | null;
  customerName: string | null;
  refusalCode: string;
  refusalLabel: string;
  refusalMessage: string;
  refusalDetails: Record<string, unknown> | null;
  staffFixable: boolean;
  posAction: "cancelled_in_pos" | "staff_action_required";
  posActionDetail: string | null;
  posCancelledAt: string | null;
  attemptCount: number;
  firstRefusedAt: string;
  refusedAt: string;
  retryRequestedAt: string | null;
  acknowledgedAt: string | null;
};

export async function fetchDispensaryPosOrderRefusals(
  getToken: () => Promise<string | null>,
  dispensaryId: string,
): Promise<{ refusals: DispensaryPosOrderRefusal[] }> {
  const path = `/api/v1/dispensaries/${encodeURIComponent(dispensaryId)}/pos-order-refusals`;
  const res = await fetchWithClerkJwt(getToken, path);
  if (!res.ok) {
    throw new Error(`pos_order_refusals_http_${res.status}`);
  }
  return (await res.json()) as { refusals: DispensaryPosOrderRefusal[] };
}

/** Staff rejected the order in the POS (or otherwise handled it): clears it from the console. */
export async function acknowledgeDispensaryPosOrderRefusal(
  getToken: () => Promise<string | null>,
  dispensaryId: string,
  refusalId: string,
): Promise<{ refusal: DispensaryPosOrderRefusal }> {
  return postPosOrderRefusalAction(getToken, dispensaryId, refusalId, "acknowledge");
}

/** Staff fixed the cause on Drop's side (menu sync, customer record); the order loop imports it again next tick. */
export async function retryDispensaryPosOrderRefusal(
  getToken: () => Promise<string | null>,
  dispensaryId: string,
  refusalId: string,
): Promise<{ refusal: DispensaryPosOrderRefusal }> {
  return postPosOrderRefusalAction(getToken, dispensaryId, refusalId, "retry");
}

async function postPosOrderRefusalAction(
  getToken: () => Promise<string | null>,
  dispensaryId: string,
  refusalId: string,
  action: "acknowledge" | "retry",
): Promise<{ refusal: DispensaryPosOrderRefusal }> {
  const path = `/api/v1/dispensaries/${encodeURIComponent(dispensaryId)}/pos-order-refusals/${encodeURIComponent(refusalId)}/${action}`;
  const res = await fetchWithClerkJwt(getToken, path, { method: "POST" });
  if (!res.ok) {
    throw new Error(`pos_order_refusal_${action}_http_${res.status}`);
  }
  return (await res.json()) as { refusal: DispensaryPosOrderRefusal };
}
